import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ateOuIgual, dataValida, hojeISO, MENSAGEM_DATA, noFuturo } from '@/lib/datas';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import CamposExtras from '@/components/base/CamposExtras';
import { validarCamposExtras } from '@/lib/camposExtras';
import DocumentosDoRegistro from '@/components/feature/DocumentosDoRegistro';
import {
  useCatalogo,
  type CampoExtra,
  type CategoriaInfraestrutura,
  type ItemDeInfraestrutura,
} from '@/lib/catalogo';
import { periodoTexto, type PeriodoParcial } from '@/lib/periodo';

interface InfraRecord {
  id: string;
  condominio_id: string;
  /** Torre a que o item pertence. Nulo = item do condomínio (migration 0043). */
  torre_id?: string | null;
  nome: string;
  /** Derivada de `categoria_id` por gatilho (migration 0049). Só leitura. */
  categoria: string;
  categoria_id?: string | null;
  /** Item do catálogo que este registro instancia. Nulo = item avulso. */
  item_catalogo_id?: string | null;
  /** Estado **operacional** do equipamento — não confundir com o do cadastro. */
  status: string;
  /** Estado do **cadastro** de compliance: em_andamento | concluido (secao 7). */
  cadastro_situacao?: 'em_andamento' | 'concluido';
  concluido_em?: string | null;
  /** Periodicidade própria. Nula enquanto o registro segue o catálogo. */
  periodicidade_manutencao_valor?: number | null;
  periodicidade_manutencao_unidade?: PeriodoParcial['unidade'];
  /** Respostas dos campos extras definidos no catálogo. */
  valores_extras?: Record<string, unknown>;
  localizacao?: string;
  ultima_manutencao?: string;
  proxima_manutencao?: string;
  responsavel?: string;
  capacidade?: string;
  observacoes?: string;
  prestador_id?: string;
}

interface TorreOption {
  id: string;
  nome: string;
}

/** Valor do select para "não é de torre nenhuma": o item é do condomínio. */
const TORRE_CONDOMINIO = '';

interface PrestadorOption {
  id: string;
  nome: string;
  tipo: string;
  servicos: string[];
  // A consulta seleciona `ativo`; registros antigos podem vir sem o campo.
  ativo?: boolean;
}

function InfraFormModal({
  item,
  prestadores,
  torres,
  condoId,
  onSave,
  onClose,
}: {
  item: InfraRecord | null;
  prestadores: PrestadorOption[];
  torres: TorreOption[];
  condoId: string;
  onSave: (data: InfraRecord) => void;
  onClose: () => void;
}) {
  // Catálogo global (migrations 0046 e 0049). A categoria deixou de ser a lista
  // fixa que estava aqui — que nem batia com o que a operação usava: o código
  // oferecia "Mobilidade" e "Sustentabilidade", e o banco tinha "Elevadores",
  // "Hidráulica" e "Combate a incêndio" (ADR-0011).
  const { itens: categorias } = useCatalogo<CategoriaInfraestrutura>('infraestrutura_categorias');
  const { itens: itensDoCatalogo } = useCatalogo<ItemDeInfraestrutura>('infraestrutura_itens');

  const [form, setForm] = useState({
    nome: item?.nome || '',
    torre_id: item?.torre_id || TORRE_CONDOMINIO,
    categoria_id: item?.categoria_id || '',
    item_catalogo_id: item?.item_catalogo_id || '',
    status: item?.status || 'Operacional',
    cadastro_situacao: item?.cadastro_situacao || 'em_andamento',
    localizacao: item?.localizacao || '',
    ultima_manutencao: item?.ultima_manutencao || '',
    proxima_manutencao: item?.proxima_manutencao || '',
    responsavel: item?.responsavel || '',
    capacidade: item?.capacidade || '',
    observacoes: item?.observacoes || '',
    periodicidade: {
      valor: item?.periodicidade_manutencao_valor ?? null,
      unidade: item?.periodicidade_manutencao_unidade ?? null,
    } as PeriodoParcial,
    valoresExtras: item?.valores_extras ?? {},
    prestador_id: item?.prestador_id || '',
  });

  const [errosDeData, setErrosDeData] = useState<Record<string, string>>({});
  const [erroCampos, setErroCampos] = useState<string | null>(null);

  const doCatalogo = useMemo(
    () => itensDoCatalogo.find((i) => i.id === form.item_catalogo_id) ?? null,
    [itensDoCatalogo, form.item_catalogo_id],
  );

  const camposExtras: CampoExtra[] = doCatalogo?.campos_extras ?? [];
  const concluido = form.cadastro_situacao === 'concluido';

  /**
   * A periodicidade que de fato vale.
   *
   * Enquanto o cadastro está em andamento, é a do catálogo — mudar a norma
   * alcança todos os itens em cadastro, que é o comportamento desejado. Ao
   * concluir, o banco congela o valor na instância (secao 7), e a partir daí é
   * o da instância que manda.
   */
  const periodicidadeEfetiva: PeriodoParcial =
    form.periodicidade.valor !== null
      ? form.periodicidade
      : {
          valor: doCatalogo?.periodicidade_manutencao_valor ?? null,
          unidade: doCatalogo?.periodicidade_manutencao_unidade ?? null,
        };

  /** Escolher um item do catálogo herda nome, categoria e periodicidade. */
  const escolherDoCatalogo = (id: string) => {
    const escolhido = itensDoCatalogo.find((i) => i.id === id);
    setForm((prev) => ({
      ...prev,
      item_catalogo_id: id,
      // Só preenche o nome se ainda estiver vazio: quem já batizou o item
      // ("Elevador Social — Torre B") não perde o nome ao vincular o catálogo.
      nome: prev.nome.trim() || (escolhido?.nome ?? ''),
      categoria_id: escolhido?.categoria_id ?? prev.categoria_id,
      // A periodicidade volta a seguir o catálogo: o valor próprio só existe
      // depois de concluído, e reescolher o item é reabrir a herança.
      periodicidade: { valor: null, unidade: null },
    }));
  };

  const prestadoresAtivos = prestadores.filter((p) => p.ativo !== false);

  /**
   * Datas de manutencao.
   *
   * Ultima manutencao e um fato: nao existe no futuro. Proxima manutencao e um
   * compromisso: nao pode preceder o fato que a originou. O gatilho da migration
   * 0014 aplica as mesmas regras no banco.
   */
  const validarDatas = (): boolean => {
    const erros: Record<string, string> = {};

    if (form.ultima_manutencao) {
      if (!dataValida(form.ultima_manutencao)) erros.ultima_manutencao = MENSAGEM_DATA.invalida;
      else if (noFuturo(form.ultima_manutencao))
        erros.ultima_manutencao = MENSAGEM_DATA.naoPodeSerFutura;
    }

    if (form.proxima_manutencao && !dataValida(form.proxima_manutencao)) {
      erros.proxima_manutencao = MENSAGEM_DATA.invalida;
    } else if (
      form.proxima_manutencao &&
      form.ultima_manutencao &&
      !erros.ultima_manutencao &&
      !ateOuIgual(form.ultima_manutencao, form.proxima_manutencao)
    ) {
      erros.proxima_manutencao = MENSAGEM_DATA.proximaAntesDaUltima;
    }

    setErrosDeData(erros);
    return Object.keys(erros).length === 0;
  };

  const handlePrestadorChange = (prestadorId: string) => {
    const selected = prestadoresAtivos.find((p) => p.id === prestadorId);
    setForm({
      ...form,
      prestador_id: prestadorId,
      responsavel: selected ? selected.nome : form.responsavel,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    if (!validarDatas()) return;

    // Os obrigatórios do catálogo só são exigidos ao concluir: obrigar a
    // capacidade da piscina antes de a pessoa ter ido medir faria o cadastro
    // não começar, e cadastro que não começa não é preenchido depois.
    if (concluido) {
      const problema = validarCamposExtras(camposExtras, form.valoresExtras);
      if (problema) {
        setErroCampos(problema);
        return;
      }
    }
    setErroCampos(null);

    onSave({
      ...item,
      ...form,
      // Vazio no select é "do condomínio", e no banco isso é nulo — não a
      // string vazia, que a FK composta recusaria.
      torre_id: form.torre_id === TORRE_CONDOMINIO ? null : form.torre_id,
      item_catalogo_id: form.item_catalogo_id || null,
      categoria_id: form.categoria_id || null,
      periodicidade_manutencao_valor: form.periodicidade.valor,
      periodicidade_manutencao_unidade: form.periodicidade.unidade,
      valores_extras: form.valoresExtras,
    } as InfraRecord);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-xl shadow-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-900">
            {item ? 'Editar Item' : 'Novo Item de Infraestrutura'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto"
        >
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nome do Item *
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              required
              placeholder="Ex: Piscina, Elevador..."
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
          </div>
          {/* Item do catálogo (secao 5.1) */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Item do catálogo
            </label>
            <select
              value={form.item_catalogo_id}
              onChange={(e) => escolherDoCatalogo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Item avulso — sem catálogo</option>
              {itensDoCatalogo.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nome}
                </option>
              ))}
            </select>
            <p className="text-xs text-foreground-500 mt-1">
              Escolher do catálogo herda a categoria e a periodicidade de manutenção, e traz os
              campos e as exigências que a plataforma mantém para este tipo de item.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Categoria
              </label>
              <select
                value={form.categoria_id}
                onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
                disabled={!!doCatalogo}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm disabled:opacity-60"
              >
                <option value="">Selecione...</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              {doCatalogo && (
                <p className="text-xs text-foreground-500 mt-1">
                  Definida pelo catálogo, junto com o item escolhido.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="Operacional">Operacional</option>
                <option value="Em Manutenção">Em Manutenção</option>
                <option value="Desativado">Desativado</option>
              </select>
            </div>
          </div>
          <div>
            <label
              htmlFor="infra-torre"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Pertence a *
            </label>
            <select
              id="infra-torre"
              value={form.torre_id}
              onChange={(e) => setForm({ ...form, torre_id: e.target.value })}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value={TORRE_CONDOMINIO}>Condomínio (área comum)</option>
              {torres.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <p className="text-xs text-foreground-400 mt-1">
              {torres.length === 0
                ? 'Nenhuma torre cadastrada ainda — o item fica como área comum do condomínio.'
                : 'Escolha a torre quando o item servir só a ela. Portaria, piscina e salão de festas costumam ser do condomínio.'}
            </p>
          </div>
          <div>
            <label
              htmlFor="infra-localizacao"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Localização
            </label>
            <input
              id="infra-localizacao"
              type="text"
              value={form.localizacao}
              onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
              placeholder="Ex: Cobertura, Subsolo 2, Casa de máquinas..."
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            />
            <p className="text-xs text-foreground-400 mt-1">
              Onde exatamente, dentro do que foi escolhido acima.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Última Manutenção
              </label>
              <input
                type="date"
                value={form.ultima_manutencao}
                max={hojeISO()}
                onChange={(e) => setForm({ ...form, ultima_manutencao: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${
                  errosDeData.ultima_manutencao
                    ? 'border-red-300 bg-red-50'
                    : 'border-background-300/60 bg-background-50'
                }`}
              />
              {errosDeData.ultima_manutencao && (
                <p className="text-xs text-red-600 mt-1">{errosDeData.ultima_manutencao}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Próxima Manutenção
              </label>
              <input
                type="date"
                value={form.proxima_manutencao}
                min={form.ultima_manutencao || undefined}
                onChange={(e) => setForm({ ...form, proxima_manutencao: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${
                  errosDeData.proxima_manutencao
                    ? 'border-red-300 bg-red-50'
                    : 'border-background-300/60 bg-background-50'
                }`}
              />
              {errosDeData.proxima_manutencao && (
                <p className="text-xs text-red-600 mt-1">{errosDeData.proxima_manutencao}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Prestador Responsável
            </label>
            <select
              value={form.prestador_id}
              onChange={(e) => handlePrestadorChange(e.target.value)}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Selecionar prestador...</option>
              {prestadoresAtivos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome} {p.tipo === 'pj' ? '(PJ)' : '(PF)'} —{' '}
                  {Array.isArray(p.servicos) ? p.servicos.slice(0, 2).join(', ') : ''}
                </option>
              ))}
            </select>
            {prestadoresAtivos.length === 0 && (
              <p className="text-xs text-foreground-400 mt-1">
                Nenhum prestador ativo cadastrado.{' '}
                <a
                  href="/admin/prestadores"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Cadastrar prestador
                </a>
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Responsável (pessoa de contato)
              </label>
              <input
                type="text"
                value={form.responsavel}
                onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                placeholder="Ex: Gilvan..."
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Capacidade
              </label>
              <input
                type="text"
                value={form.capacidade}
                onChange={(e) => setForm({ ...form, capacidade: e.target.value })}
                placeholder="Ex: 80 pessoas..."
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Observações
            </label>
            <textarea
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-y"
            />
            <p className="text-xs text-foreground-400 mt-1">
              {form.observacoes.length}/1000 caracteres
            </p>
          </div>
          {/* Campos extras definidos pelo catálogo */}
          <CamposExtras
            campos={camposExtras}
            valores={form.valoresExtras}
            onChange={(valoresExtras) => setForm({ ...form, valoresExtras })}
          />
          {erroCampos && (
            <p role="alert" className="text-xs text-red-600">
              {erroCampos}
            </p>
          )}

          {/* Periodicidade de manutenção — era `recorrencia`, um jsonb com texto
              livre ('mensal', 'Mensal', 'mês'), substituído pelo Período. */}
          <div className="p-4 bg-background-100/50 rounded-lg border border-background-200/70 space-y-3">
            {doCatalogo && form.periodicidade.valor === null ? (
              <div>
                <p className="text-sm font-medium text-foreground-700">
                  Manutenção: {periodoTexto(periodicidadeEfetiva)}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  Herdada do catálogo. Enquanto o cadastro estiver em andamento, ela acompanha as
                  correções que a plataforma fizer; ao concluir, o valor é congelado aqui.
                </p>
              </div>
            ) : (
              <CampoPeriodo
                rotulo="Periodicidade de manutenção"
                valor={form.periodicidade}
                onChange={(periodicidade) => setForm({ ...form, periodicidade })}
                textoVazio="item sem manutenção recorrente."
              />
            )}

            {/* Situação do cadastro (secao 7) */}
            <div className="pt-3 border-t border-background-200/70">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Situação do cadastro
              </label>
              <select
                value={form.cadastro_situacao}
                onChange={(e) =>
                  setForm({
                    ...form,
                    cadastro_situacao: e.target.value as 'em_andamento' | 'concluido',
                  })
                }
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="em_andamento">Em andamento — segue o catálogo</option>
                <option value="concluido">Concluído — congela os valores herdados</option>
              </select>
              <p className="text-xs text-foreground-500 mt-1">
                {concluido
                  ? 'Ao gravar, os valores herdados viram cópia deste item: mudanças no catálogo não o alcançam mais.'
                  : 'O cadastro acompanha o catálogo. Conclua quando as informações estiverem conferidas.'}
              </p>
            </div>
          </div>

          {/* Documentos do item (componente 3.3) — só depois de gravado, porque
              o caminho no bucket precisa do id. */}
          {item?.id && (
            <DocumentosDoRegistro
              dono={{ tipo: 'infraestrutura', id: item.id }}
              condominioId={condoId}
              titulo="Documentos do item"
              tipos={['avulso', 'treinamento', 'alvara']}
              chaveDeAlerta="manutencao"
            />
          )}
          <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {item ? 'Salvar Alterações' : 'Cadastrar Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InfraCalendario({
  itens,
  selectedDate,
  onSelectDate,
  condoId,
}: {
  itens: InfraRecord[];
  selectedDate: string | null;
  onSelectDate: (d: string | null) => void;
  condoId: string;
}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const meses = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];
  const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const eventosPorData: Record<string, { item: InfraRecord; type: 'ultima' | 'proxima' }[]> = {};
  itens.forEach((item) => {
    if (item.ultima_manutencao) {
      if (!eventosPorData[item.ultima_manutencao]) eventosPorData[item.ultima_manutencao] = [];
      eventosPorData[item.ultima_manutencao].push({ item, type: 'ultima' });
    }
    if (item.proxima_manutencao) {
      if (!eventosPorData[item.proxima_manutencao]) eventosPorData[item.proxima_manutencao] = [];
      eventosPorData[item.proxima_manutencao].push({ item, type: 'proxima' });
    }
  });

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const todayStr = today.toISOString().slice(0, 10);
  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
    onSelectDate(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
    onSelectDate(null);
  };
  const formatData = (iso?: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const selectedEvents = selectedDate ? eventosPorData[selectedDate] || [] : [];
  const monthEvents = Object.entries(eventosPorData)
    .filter(([date]) => date.startsWith(`${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`))
    .flatMap(([date, evs]) => evs.map((ev) => ({ ...ev, date })))
    .sort((a, b) => a.date.localeCompare(b.date));
  const eventsToShow = selectedDate
    ? selectedEvents.map((ev) => ({ ...ev, date: selectedDate }))
    : monthEvents;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex-1 min-w-0">
        <div className="flex items-center justify-between mb-5">
          <button
            onClick={prevMonth}
            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <h4 className="text-sm font-semibold text-foreground-900">
            {meses[viewMonth]} {viewYear}
          </h4>
          <button
            onClick={nextMonth}
            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {diasSemana.map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-foreground-500 py-1.5">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`e-${i}`} className="aspect-square"></div>
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hasEvents = !!eventosPorData[dateStr];
            const evts = eventosPorData[dateStr] || [];
            const hasProxima = evts.some((e) => e.type === 'proxima');
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            return (
              <button
                key={day}
                onClick={() => onSelectDate(isSelected ? null : dateStr)}
                className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all cursor-pointer ${isSelected ? 'bg-primary-500 text-background-50' : isToday ? 'bg-primary-50 text-primary-700 font-semibold ring-1 ring-primary-200' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                <span className="text-xs leading-none">{day}</span>
                {hasEvents && (
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {hasProxima && (
                      <span
                        className={`w-1 h-1 rounded-full ${isSelected ? 'bg-background-50' : 'bg-accent-500'}`}
                      ></span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 w-full lg:w-80 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-foreground-900">
            {selectedDate
              ? `Manutenções em ${formatData(selectedDate)}`
              : `Agenda de ${meses[viewMonth]}`}
          </h4>
          {selectedDate && (
            <button
              onClick={() => onSelectDate(null)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              <i className="ri-close-line mr-1"></i>Limpar
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2.5 max-h-[420px] overflow-y-auto">
          {eventsToShow.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-2">
                <i className="ri-calendar-check-line text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">
                {selectedDate ? 'Nenhuma manutenção nesta data.' : 'Nenhuma manutenção este mês.'}
              </p>
            </div>
          ) : (
            eventsToShow.map((ev, idx) => (
              <Link
                key={idx}
                to={`/condominios/${condoId}/infraestrutura/${ev.item.id}`}
                className="flex items-start gap-3 p-3 rounded-md border border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer"
              >
                <div
                  className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${ev.type === 'proxima' ? 'bg-accent-500' : 'bg-foreground-300'}`}
                ></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800 truncate">{ev.item.nome}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-foreground-500">
                      {ev.type === 'proxima' ? 'Próxima' : 'Última'}
                      {!selectedDate && ` — ${formatData(ev.date)}`}
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function InfraestruturaTab() {
  const { id: condoId } = useParams<{ id: string }>();
  const [itens, setItens] = useState<InfraRecord[]>([]);
  const [prestadores, setPrestadores] = useState<PrestadorOption[]>([]);
  const [torres, setTorres] = useState<TorreOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InfraRecord | null>(null);
  const [editTarget, setEditTarget] = useState<InfraRecord | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<'cards' | 'calendario'>('cards');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchItens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .from('infraestrutura')
        .select('*')
        .eq('condominio_id', condoId)
        .order('nome', { ascending: true });
      if (fErr) setError(fErr.message);
      else setItens((data || []) as InfraRecord[]);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  // Só as ativas: uma torre desativada não deve receber equipamento novo. As já
  // vinculadas a ela continuam apontando, e o nome é resolvido pelo mapa abaixo.
  const fetchTorres = useCallback(async () => {
    if (!condoId) return;
    try {
      const { data, error: tErr } = await supabase
        .from('torres')
        .select('id, nome')
        .eq('condominio_id', condoId)
        .eq('ativo', true)
        .order('nome', { ascending: true });
      if (!tErr) setTorres((data || []) as TorreOption[]);
    } catch {
      /* silencioso */
    }
  }, [condoId]);

  const fetchPrestadores = useCallback(async () => {
    try {
      const { data, error: pErr } = await supabase
        .from('prestadores')
        .select('id, nome, tipo, servicos, ativo')
        .eq('ativo', true)
        .order('nome', { ascending: true });
      if (!pErr) setPrestadores((data || []) as PrestadorOption[]);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    if (!condoId) return;
    void fetchItens();
    void fetchPrestadores();
    void fetchTorres();
  }, [condoId, fetchItens, fetchPrestadores, fetchTorres]);

  const nomeDaTorre = (torreId?: string | null) =>
    torreId ? (torres.find((t) => t.id === torreId)?.nome ?? 'Torre removida') : 'Condomínio';

  const getPrestadorNome = (prestadorId?: string) => {
    if (!prestadorId) return null;
    const p = prestadores.find((x) => x.id === prestadorId);
    return p ? p.nome : null;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase
      .from('infraestrutura')
      .delete()
      .eq('id', deleteTarget.id);
    if (dErr) {
      showToast(`Erro: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }
    setItens((prev) => prev.filter((i) => i.id !== deleteTarget.id));
    showToast(`"${deleteTarget.nome}" excluído com sucesso.`, 'success');
    setDeleteTarget(null);
  };

  const handleSave = async (data: InfraRecord) => {
    // `categoria` (texto) **não** entra: ela virou derivada de `categoria_id`
    // por gatilho (migration 0049). Mandá-la daqui seria reabrir a divergência
    // entre o id escolhido e o texto gravado.
    const payload: any = {
      nome: data.nome,
      categoria_id: data.categoria_id || null,
      item_catalogo_id: data.item_catalogo_id || null,
      status: data.status,
      cadastro_situacao: data.cadastro_situacao || 'em_andamento',
      localizacao: (data as any).localizacao,
      torre_id: (data as any).torre_id || null,
      ultima_manutencao: (data as any).ultima_manutencao || null,
      proxima_manutencao: (data as any).proxima_manutencao || null,
      responsavel: (data as any).responsavel,
      capacidade: (data as any).capacidade,
      observacoes: (data as any).observacoes,
      periodicidade_manutencao_valor: data.periodicidade_manutencao_valor ?? null,
      periodicidade_manutencao_unidade: data.periodicidade_manutencao_unidade ?? null,
      valores_extras: data.valores_extras ?? {},
      prestador_id: (data as any).prestador_id || null,
      condominio_id: condoId,
    };

    if (payload.prestador_id === '') payload.prestador_id = null;

    if (editTarget) {
      const { error: uErr } = await supabase
        .from('infraestrutura')
        .update(payload)
        .eq('id', editTarget.id);
      if (uErr) {
        showToast(`Erro: ${uErr.message}`, 'error');
        return;
      }
      // Relê em vez de aplicar o payload ao estado: o banco preenche `categoria`
      // pelo gatilho e congela a periodicidade na conclusão (secao 7). Confiar
      // no payload mostraria a tela sem esses valores até o próximo F5.
      await fetchItens();
      showToast(`"${payload.nome}" atualizado com sucesso.`, 'success');
    } else {
      const { data: inserted, error: iErr } = await supabase
        .from('infraestrutura')
        .insert([payload])
        .select('*')
        .single();
      if (iErr) {
        showToast(`Erro: ${iErr.message}`, 'error');
        return;
      }
      void inserted;
      await fetchItens();
      showToast(`"${payload.nome}" cadastrado com sucesso.`, 'success');
    }
    setShowForm(false);
    setEditTarget(null);
  };

  const categoriasDisponiveis = [...new Set(itens.map((i) => i.categoria))].sort();
  const filtrados = itens.filter((item) => {
    return (
      item.nome.toLowerCase().includes(busca.toLowerCase()) &&
      (!filtroCategoria || item.categoria === filtroCategoria) &&
      (!filtroStatus || item.status === filtroStatus)
    );
  });

  const formatData = (iso?: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };
  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      Operacional: 'bg-green-50 text-green-700 border-green-200',
      'Em Manutenção': 'bg-amber-50 text-amber-700 border-amber-200',
      Desativado: 'bg-red-50 text-red-700 border-red-200',
    };
    return map[status] || 'bg-background-100 text-foreground-700 border-background-300';
  };
  const categoriaBadge = (cat: string) => {
    const map: Record<string, string> = {
      Lazer: 'bg-accent-100 text-accent-900 border-accent-300',
      Segurança: 'bg-secondary-100 text-secondary-900 border-secondary-200',
      Mobilidade: 'bg-primary-50 text-primary-700 border-primary-200',
      Serviços: 'bg-secondary-100 text-secondary-900 border-secondary-200',
      Sustentabilidade: 'bg-green-50 text-green-700 border-green-200',
    };
    return map[cat] || 'bg-background-100 text-foreground-700 border-background-300';
  };
  const categoriaIcon = (cat: string) => {
    const map: Record<string, string> = {
      Lazer: 'ri-football-line',
      Segurança: 'ri-shield-check-line',
      Mobilidade: 'ri-arrow-up-down-line',
      Serviços: 'ri-tools-line',
      Sustentabilidade: 'ri-leaf-line',
    };
    return map[cat] || 'ri-building-line';
  };

  const contagemPorStatus = {
    todos: itens.length,
    operacional: itens.filter((i) => i.status === 'Operacional').length,
    manutencao: itens.filter((i) => i.status === 'Em Manutenção').length,
    desativado: itens.filter((i) => i.status === 'Desativado').length,
  };
  const todayStr = new Date().toISOString().slice(0, 10);
  const itensAtrasados = itens.filter(
    (i) => i.proxima_manutencao && i.proxima_manutencao < todayStr && i.status !== 'Desativado',
  );
  const itensProximos = itens.filter((i) => {
    if (!i.proxima_manutencao || i.status === 'Desativado' || i.proxima_manutencao < todayStr)
      return false;
    const diff = Math.ceil(
      (new Date(i.proxima_manutencao).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
    );
    return diff >= 0 && diff <= 15;
  });

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={fetchItens}
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">
            Infraestrutura do Condomínio
          </h3>
          <p className="text-sm text-foreground-500 mt-0.5">
            {contagemPorStatus.operacional} operacional(is), {contagemPorStatus.manutencao} em
            manutenção, {contagemPorStatus.desativado} desativado(s)
          </p>
        </div>
        <button
          onClick={() => {
            setEditTarget(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>Novo Item
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', valor: contagemPorStatus.todos },
          { label: 'Operacional', valor: contagemPorStatus.operacional },
          { label: 'Em Manutenção', valor: contagemPorStatus.manutencao },
          { label: 'Desativado', valor: contagemPorStatus.desativado },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-background-50 border border-background-200/70 rounded-lg p-4"
          >
            <p className="text-xs font-medium text-foreground-500">{card.label}</p>
            <p className="text-2xl font-bold mt-1 text-foreground-900">{card.valor}</p>
          </div>
        ))}
      </div>

      {(itensAtrasados.length > 0 || itensProximos.length > 0) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {itensAtrasados.length > 0 && (
            <div className="flex-1 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-600 text-sm"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-red-800">
                  {itensAtrasados.length} item(ns) com manutenção atrasada
                </p>
              </div>
            </div>
          )}
          {itensProximos.length > 0 && (
            <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-timer-line text-amber-600 text-sm"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">
                  {itensProximos.length} item(ns) com vencimento em até 15 dias
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          />
        </div>
        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todas as categorias</option>
          {categoriasDisponiveis.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todos os status</option>
          <option value="Operacional">Operacional</option>
          <option value="Em Manutenção">Em Manutenção</option>
          <option value="Desativado">Desativado</option>
        </select>
      </div>

      <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setViewMode('cards')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${viewMode === 'cards' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
        >
          <i className="ri-layout-grid-line mr-1.5"></i>Cards
        </button>
        <button
          onClick={() => setViewMode('calendario')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${viewMode === 'calendario' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
        >
          <i className="ri-calendar-line mr-1.5"></i>Calendário
        </button>
      </div>

      {viewMode === 'cards' ? (
        filtrados.length === 0 ? (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-tools-line text-foreground-400 text-xl"></i>
            </div>
            <p className="text-sm font-medium text-foreground-700">
              {itens.length === 0 ? 'Nenhum item cadastrado' : 'Nenhum item encontrado'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtrados.map((item) => {
              const itemOverdue =
                item.proxima_manutencao &&
                item.proxima_manutencao < todayStr &&
                item.status !== 'Desativado';
              const prestadorNome = getPrestadorNome(item.prestador_id);
              return (
                <div
                  key={item.id}
                  className={`bg-background-50 border rounded-lg overflow-hidden hover:border-background-300 transition-colors ${itemOverdue ? 'border-red-300' : 'border-background-200/70'}`}
                >
                  <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${categoriaBadge(item.categoria).split(' ')[0]}`}
                      >
                        <i
                          className={`${categoriaIcon(item.categoria)} text-sm ${categoriaBadge(item.categoria).split(' ')[1]}`}
                        ></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900 truncate">
                          {item.nome}
                        </p>
                        {/* Classificar sem ver o resultado é pedir para o dado
                            apodrecer: o vínculo aparece no próprio cartão. */}
                        <p className="text-xs text-foreground-500 truncate mt-0.5">
                          <i className="ri-building-line mr-1"></i>
                          {nomeDaTorre(item.torre_id)}
                          {item.localizacao ? ` · ${item.localizacao}` : ''}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${statusBadge(item.status)}`}
                    >
                      {item.status === 'Em Manutenção' ? 'Manutenção' : item.status}
                    </span>
                  </div>
                  <div className="p-4 flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-foreground-500">Próx. manutenção</span>
                        <p className="font-medium text-foreground-800 mt-0.5">
                          {formatData(item.proxima_manutencao)}
                        </p>
                      </div>
                      <div>
                        <span className="text-foreground-500">Responsável</span>
                        <p className="font-medium text-foreground-800 mt-0.5">
                          {item.responsavel || '-'}
                        </p>
                      </div>
                    </div>
                    {prestadorNome && (
                      <div className="flex items-center gap-1.5 text-xs text-foreground-500 mt-1">
                        <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                          <i className="ri-building-2-line text-[10px]"></i>
                        </div>
                        <span className="truncate">{prestadorNome}</span>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-2.5 border-t border-background-200/70 flex items-center justify-end gap-1 bg-background-100/30">
                    <button
                      onClick={() => {
                        setEditTarget(item);
                        setShowForm(true);
                      }}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                      title="Editar"
                    >
                      <i className="ri-pencil-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                      title="Excluir"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <InfraCalendario
          itens={filtrados}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          condoId={condoId || ''}
        />
      )}

      {showForm && (
        <InfraFormModal
          item={editTarget}
          prestadores={prestadores}
          torres={torres}
          condoId={condoId}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditTarget(null);
          }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir <strong>{deleteTarget.nome}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : toast.type === 'error' ? 'ri-close-circle-line text-red-600' : 'ri-information-line text-blue-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
